// packages/web-common/src/plugins/PluginPageHost.tsx
import { useEffect, useRef, useState } from 'react';
import type { PluginPage } from './types';

interface Props {
  page: PluginPage;
}

interface PluginModule {
  [key: string]: ((args: { container: HTMLElement }) => { unmount?: () => void } | void) | unknown;
}

/**
 * Loads a plugin's pre-built ES-module bundle and mounts its
 * `federated_module` export into a host-provided <div>.
 *
 * Contract: the plugin's bundle MUST export a named function matching
 * `federated_module` (typically `MainPage`) that takes a single
 * `{ container }` object and renders into it. The function MAY
 * return `{ unmount }` so the host can clean up on route change.
 *
 * For P0.1 there's no React sharing — the plugin's bundle is a
 * self-contained vanilla-DOM render. Plugin authors who want React-
 * component integration get importmap support in a later iteration.
 */
export function PluginPageHost({ page }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    let unmountFn: (() => void) | undefined;
    let cancelled = false;

    const url = `/plugins-static/${page.plugin_id}/index.js`;
    import(/* @vite-ignore */ url)
      .then((mod: PluginModule) => {
        if (cancelled) return;
        const render = mod[page.federated_module];
        if (typeof render !== 'function') {
          setError(
            `Plugin bundle has no export named "${page.federated_module}". ` +
              `Check the plugin author's vite.config.ts — the named export must ` +
              `match the federated_module declared in plugin.toml.`,
          );
          setLoading(false);
          return;
        }
        try {
          const result = (render as (a: { container: HTMLElement }) => { unmount?: () => void } | void)({
            container,
          });
          if (result && typeof result.unmount === 'function') {
            unmountFn = result.unmount;
          }
          setLoading(false);
        } catch (renderErr) {
          // eslint-disable-next-line no-console
          console.error('[PluginPageHost] render failed', renderErr);
          setError(`Plugin render failed: ${renderErr}`);
          setLoading(false);
        }
      })
      .catch((importErr) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[PluginPageHost] dynamic import failed', importErr);
        setError(
          `Failed to load plugin bundle from ${url}. ` +
            `Verify the panel was started with HPANEL_DEV_PLUGINS=1 and the ` +
            `plugin has a frontend/index.js file. (${importErr?.message ?? importErr})`,
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (unmountFn) {
        try {
          unmountFn();
        } catch (unmountErr) {
          // eslint-disable-next-line no-console
          console.error('[PluginPageHost] unmount threw', unmountErr);
        }
      }
    };
  }, [page.plugin_id, page.federated_module]);

  return (
    <div data-testid={`plugin-page-${page.plugin_id}`}>
      {loading && <p>Loading plugin…</p>}
      {error && (
        <div role="alert" style={{ padding: '1rem', color: '#b00' }}>
          {error}
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}
